/* istanbul ignore file */

import { useCallback } from 'react';

import { type DressingWeight, ModifierGroupVariation } from '@sg/graphql-schema';

import { type CustomizationMachineTypes, getPortionOptions, getUpsellPrice } from '../../machine';
import { type CustomizationMachineActorRef } from '../../machine/customization.machine';
import { useCustomizationContext } from '../useCustomizationContext';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Customization helpers to interact with the customization machine.
 */
export const useCustomizationHelpers = (params: Params) => {
  const { actor } = params;

  const { send } = actor;

  // ─── Context ─────────────────────────────────────────────────────────

  const {
    allModificationsMap,
    activeModifications,
    activeModificationsMap,
    activeModificationsCountByGroups,
    maxModificationsByGroups,
    extrasModificationsMap,
    modificationLimits,
    includedGroupCounts,
    initialIngredientQuantities,
    isUpsellPricingEnabled,
  } = useCustomizationContext({ actor });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const startCustomization = useCallback(() => {
    send({ type: 'customization.start' });
  }, [send]);

  const continueCustomization = useCallback(() => {
    send({ type: 'customization.continue' });
  }, [send]);

  const endCustomization = useCallback(() => {
    send({ type: 'customization.complete' });
  }, [send]);

  const completeCustomization = useCallback(() => {
    send({ type: 'customization.complete' });
  }, [send]);

  const selectDetails = useCallback(() => {
    send({ type: 'customization.complete' });
  }, [send]);

  const retryFetchCustomizationData = useCallback(() => {
    send({ type: 'customization.retry-fetch' });
  }, [send]);

  const addIngredient = useCallback(
    (ingredientId: string) => {
      send({ type: 'customization.add-modification', ingredientId });
    },
    [send],
  );

  const removeIngredient = useCallback(
    (ingredientId: string) => {
      send({ type: 'customization.remove-modification', ingredientId });
    },
    [send],
  );

  const removeIngredientCompletely = useCallback(
    (ingredientId: string) => {
      const modification = allModificationsMap?.get(ingredientId);
      const isSplitPortion = modification?.modificationGroupVariation === ModifierGroupVariation.SplitPortion; // prettier-ignore

      if (isSplitPortion) {
        send({ type: 'customization.remove-split-portion-completely', ingredientId }); // prettier-ignore

        return;
      }

      send({ type: 'customization.remove-modification-completely', ingredientId }); // prettier-ignore
    },
    [allModificationsMap, send],
  );

  const setDressingModeToOnTheSide = useCallback(() => {
    actor.send({ type: 'customization.dressing-mode.on-the-side' });
  }, [actor]);

  const setDressingModeToMixedIn = useCallback(() => {
    actor.send({ type: 'customization.dressing-mode.mixed-in' });
  }, [actor]);

  const changeDressingPortions = useCallback(
    (ingredientId: string, portions: number) => {
      send({
        type: 'customization.dressing.change-portions',
        ingredientId,
        portions,
      });
    },
    [send],
  );

  const changeDressingWeight = useCallback(
    (ingredientId: string, weight: DressingWeight) => {
      send({
        type: 'customization.dressing.change-weight',
        ingredientId,
        weight,
      });
    },
    [send],
  );

  const toggleSplitPortion = useCallback(
    (ingredientId: string) => {
      send({ type: 'customization.toggle-split-portion', ingredientId });
    },
    [send],
  );

  // ─── Derived Helpers ─────────────────────────────────────────────────

  const getGroupCounts = useCallback(
    (groupName: string) => {
      const activeModificationsCount = activeModificationsCountByGroups[groupName] ?? 0; // prettier-ignore
      const maxModificationsCountForGroup = maxModificationsByGroups[groupName];

      const hasValidMaxCount = checkIfValidCount(maxModificationsCountForGroup);

      if (!hasValidMaxCount) return;

      return {
        activeModificationsCount,
        maxModificationsCountForGroup,
      };
    },
    [activeModificationsCountByGroups, maxModificationsByGroups],
  );

  const getFormattedQuantity = useCallback(
    (groupName: string) => {
      const counts = getGroupCounts(groupName);

      if (!counts) return;

      const { activeModificationsCount, maxModificationsCountForGroup } = counts;

      return `${activeModificationsCount}/${maxModificationsCountForGroup}`;
    },
    [getGroupCounts],
  );

  const checkIfCanAddMoreModifications = useCallback(
    (groupName: string) => {
      const counts = getGroupCounts(groupName);

      if (!counts) return false;

      const { activeModificationsCount, maxModificationsCountForGroup } = counts;

      return activeModificationsCount < maxModificationsCountForGroup;
    },
    [getGroupCounts],
  );

  const getMaxPortionsForGroup = useCallback(
    (groupName: string) => {
      const counts = getGroupCounts(groupName);

      return counts?.maxModificationsCountForGroup ?? Number.POSITIVE_INFINITY;
    },
    [getGroupCounts],
  );

  const getUpsellPriceForIngredient = useCallback(
    (ingredientId: string | undefined) =>
      getUpsellPrice({
        ingredientId,
        allModificationsMap,
        extrasModificationsMap,
        modificationLimits,
        includedGroupCounts,
        initialIngredientQuantities,
      }),
    [
      allModificationsMap,
      extrasModificationsMap,
      modificationLimits,
      includedGroupCounts,
      initialIngredientQuantities,
    ],
  );

  const getPortionOptionsForIngredient = useCallback(
    (ingredientId: string | undefined) => {
      if (!isUpsellPricingEnabled || !ingredientId || !allModificationsMap) return;

      const modification = allModificationsMap.get(ingredientId);

      if (!modification) return;
      if (modification.modificationGroupVariation !== ModifierGroupVariation.PortionStepper) return;

      const { modificationGroupName } = modification;

      const maxPortions = maxModificationsByGroups[modificationGroupName] ?? 0;
      const freePortions = modificationLimits[modificationGroupName]?.maxAggregateQuantity ?? 0;
      const currentPortions = activeModificationsMap.get(modification.id)?.quantity ?? 0;
      const groupActiveCount = activeModificationsCountByGroups[modificationGroupName] ?? 0;

      return getPortionOptions({
        ingredientId,
        currentPortions,
        groupActiveCount,
        maxPortions,
        freePortions,
        allModificationsMap,
        extrasModificationsMap,
        initialIngredientQuantities,
        activeModifications,
      });
    },
    [
      allModificationsMap,
      activeModifications,
      activeModificationsMap,
      activeModificationsCountByGroups,
      maxModificationsByGroups,
      extrasModificationsMap,
      isUpsellPricingEnabled,
      modificationLimits,
      initialIngredientQuantities,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    startCustomization,
    endCustomization,
    continueCustomization,
    completeCustomization,
    selectDetails,
    retryFetchCustomizationData,
    addIngredient,
    removeIngredient,
    removeIngredientCompletely,
    setDressingModeToOnTheSide,
    setDressingModeToMixedIn,
    getMaxPortionsForGroup,
    getFormattedQuantity,
    checkIfCanAddMoreModifications,
    changeDressingPortions,
    changeDressingWeight,
    toggleSplitPortion,
    sortModifications,
    getUpsellPriceForIngredient,
    getPortionOptionsForIngredient,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfValidCount(count: unknown) {
  return typeof count === 'number' && !Number.isNaN(count);
}

function sortModifications(
  initialModificationsMap: CustomizationMachineTypes.ModificationsMap,
  modificationGroup: CustomizationMachineTypes.ProductModifierGroup,
) {
  const unsorted = modificationGroup.modifications;

  return [...unsorted].sort((modification, nextModification) => {
    const { id } = modification;
    const { id: nextId } = nextModification;

    const isModificationInitial = initialModificationsMap.has(id);
    const isNextModificationInitial = initialModificationsMap.has(nextId);

    if (isModificationInitial && !isNextModificationInitial) return -1;
    if (!isModificationInitial && isNextModificationInitial) return 1;

    return 0;
  });
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  actor: CustomizationMachineActorRef;
};
